import { type FC, type RefObject } from 'react';
import { Header, HStack, Label, VStack } from '@direct-frontend/components';
import { DASHBOARD_TODO_LIST_FEATURE_HINT_IDS } from '../../../../lib/featureHints';
import { GalleryWithPaginatorLazy } from '../../../../ui/GalleryWithPaginator';
import { MarkdownAsync } from '../../../Markdown';
import { type DashboardTodoListItemProps } from '../../DashboardTodoListItem.types';
import { useDashboardTodoListItem } from '../../useDashboardTodoListItem';
import { DashboardTodoListItemCompletionIcon } from '../CompletionIcon/CompletionIcon';
import { DashboardTodoListItemFeatureHintSlot } from '../FeatureHintSlot/DashboardTodoListItemFeatureHintSlot';
import styles from '../../DashboardTodoListItem.module.css';

export type {
    DashboardTodoListItemFeatureHintsConfig,
    DashboardTodoListItemProps,
} from '../../DashboardTodoListItem.types';

export const DashboardTodoListItem: FC<DashboardTodoListItemProps> = (props) => {
    const {
        rootRef,
        rootClassName,
        onRootClick,
        dataTestId,
        item,
        isExpanded,
        toggle,
        isCompleteUpdating,
        galleryItems,
        showNewLabel,
        featureHints,
    } = useDashboardTodoListItem(props);

    const { onToggleItemComplete, onMarkdownLinkClick } = props;

    const titleHeading = (
        <Header className={styles.titleHeading} level="h4">
            {item.title}
        </Header>
    );

    const completionIcon = (
        <DashboardTodoListItemCompletionIcon
            data-testid={dataTestId}
            isCompleteUpdating={isCompleteUpdating}
            item={item}
            onToggleItemComplete={onToggleItemComplete}
        />
    );

    const newLabel = (
        <Label color="success" data-testid={dataTestId ? `${dataTestId}.NewLabel` : undefined} size="3xs">
            New
        </Label>
    );

    const hasGallery = galleryItems.length > 0;
    const showDescription = isExpanded && Boolean(item.description);
    const showMedia = isExpanded && hasGallery;

    return (
        <div ref={rootRef as RefObject<HTMLDivElement>} onClick={onRootClick} className={rootClassName}>
            <HStack data-testid={dataTestId} gap="20">
                <VStack gap="12">
                    <div
                        role="button"
                        tabIndex={0}
                        className={styles.head}
                        aria-expanded={isExpanded}
                        onClick={toggle}
                        data-testid={dataTestId ? `${dataTestId}.Toggle` : undefined}
                    >
                        <HStack gap="12" alignItems="flex-start">
                            <DashboardTodoListItemFeatureHintSlot
                                data-testid={dataTestId}
                                featureHints={featureHints}
                                hintId={DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON}
                                placement="bottom"
                                testIdSuffix="CompletionIcon"
                            >
                                {completionIcon}
                            </DashboardTodoListItemFeatureHintSlot>
                            <HStack
                                gap="8"
                                alignItems="center"
                                className={styles.titleWithBadge}
                                flex="1"
                                minWidth="0px"
                            >
                                <DashboardTodoListItemFeatureHintSlot
                                    data-testid={dataTestId}
                                    featureHints={featureHints}
                                    hintId={DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE}
                                    placement="bottom"
                                    testIdSuffix="Title"
                                >
                                    {titleHeading}
                                </DashboardTodoListItemFeatureHintSlot>
                                {showNewLabel ? (
                                    <DashboardTodoListItemFeatureHintSlot
                                        data-testid={dataTestId}
                                        featureHints={featureHints}
                                        hintId={DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL}
                                        placement="right"
                                        testIdSuffix="NewLabel"
                                    >
                                        {newLabel}
                                    </DashboardTodoListItemFeatureHintSlot>
                                ) : null}
                            </HStack>
                        </HStack>
                    </div>
                    {showDescription ? (
                        <div className={styles.description}>
                            <MarkdownAsync
                                data-testid={dataTestId ? `${dataTestId}.Markdown` : undefined}
                                onLinkClick={
                                    onMarkdownLinkClick
                                        ? ({ href }) => {
                                              onMarkdownLinkClick(href);
                                          }
                                        : undefined
                                }
                            >
                                {item.description}
                            </MarkdownAsync>
                        </div>
                    ) : null}
                </VStack>
                {showMedia ? (
                    <HStack className={styles.media} width="568px">
                        <GalleryWithPaginatorLazy
                            data-testid={dataTestId ? `${dataTestId}.Gallery` : undefined}
                            items={galleryItems}
                            itemGapSize="8"
                            hidePaginationForSingleItem
                            renderItemContent={(slide, _currentSlideIndex, shouldLoadSrc) => (
                                // eslint-disable-next-line @direct-frontend/components/no-restricted-native-elements -- автоматический игнор в рамках DIRECT-327349
                                <img
                                    className={styles.slideImage}
                                    draggable={false}
                                    src={shouldLoadSrc ? slide.src : undefined}
                                    alt=""
                                    width={568}
                                    height={240}
                                    loading="lazy"
                                />
                            )}
                            arrowButtons={{
                                className: styles.galleryArrowButtons,
                            }}
                        />
                    </HStack>
                ) : null}
            </HStack>
        </div>
    );
};
